/**
 * IcraPermitsTab
 * --------------
 * ASHE Infection Control Risk Assessment permits. Activity Type A–D crossed
 * with Patient Risk Group 1–4 gives the Class of Precautions I–IV; Class III
 * and IV cannot go active without Infection Prevention and Safety signatures.
 * Every permit prints a door sign with a QR code to the live permit.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Download, Trash2, ShieldCheck, PenLine, CheckCircle2,
  AlertTriangle, ClipboardCheck, QrCode, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API } from "@/App";

const CLASS_STYLE = {
  I: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  II: "bg-blue-50 text-blue-700 ring-blue-300",
  III: "bg-amber-50 text-amber-700 ring-amber-300",
  "III/IV": "bg-red-50 text-red-700 ring-red-300",
  IV: "bg-red-50 text-red-700 ring-red-300",
};

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  pending_signatures: "bg-amber-50 text-amber-700 ring-amber-300",
  active: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  closed: "bg-slate-100 text-slate-500 ring-slate-300",
  void: "bg-slate-100 text-slate-400 ring-slate-300",
};

const blank = () => ({
  work_description: "", activity_type: "B", patient_risk_group: "2",
  building: "", floor: "", department: "", room_numbers: "", adjacent_areas: "",
  start_date: "", end_date: "", work_hours: "",
  responsible_company: "", contractor_foreman: "", foreman_phone: "",
  infection_prevention_contact: "", safety_contact: "",
  barrier_type: "", negative_air_units: 0, hepa_filtration: false,
  debris_route: "", traffic_route: "",
  water_shutdown: false, fire_alarm_impairment: false, ilsm_required: false,
  additional_requirements: "", notes: "",
});

const Stat = ({ label, value, accent = "text-slate-900", sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`text-lg font-black mt-1 ${accent}`}>{value}</p>
    {sub ? <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p> : null}
  </div>
);

const Label = ({ children }) => (
  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{children}</label>
);

export default function IcraPermitsTab({ projectId, project, token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [signing, setSigning] = useState(null);
  const [sign, setSign] = useState({ role: "infection_prevention", name: "", title: "" });
  const [checking, setChecking] = useState(null);
  const [checkItems, setCheckItems] = useState({});
  const [checkNotes, setCheckNotes] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        axios.get(`${API}/icra-permits?project_id=${projectId}`, { headers }),
        axios.get(`${API}/icra-permits/matrix`, { headers }),
      ]);
      setRows(r.data?.permits || []);
      setSummary(r.data?.summary || null);
      setMeta(m.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load ICRA permits");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const liveClass = useMemo(() => {
    if (!form || !meta) return null;
    return meta.matrix?.[String(form.patient_risk_group)]?.[String(form.activity_type)] || null;
  }, [form, meta]);

  const save = async () => {
    if (!form.work_description.trim()) return toast.error("Describe the work being permitted");
    setSaving(true);
    try {
      const body = { ...form, negative_air_units: Number(form.negative_air_units || 0) };
      const r = editingId
        ? await axios.put(`${API}/icra-permits/${editingId}`, body, { headers })
        : await axios.post(`${API}/icra-permits`, { project_id: projectId, ...body }, { headers });
      toast.success(`${r.data.permit.number} saved`, {
        description: `Class ${r.data.permit.precaution_class} precautions`,
      });
      setForm(null); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the permit");
    } finally { setSaving(false); }
  };

  const doSign = async () => {
    if (!sign.name.trim()) return toast.error("Type the signer's name");
    try {
      await axios.post(`${API}/icra-permits/${signing.id}/sign`, sign, { headers });
      toast.success(`${signing.number} signed`);
      setSigning(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the signature");
    }
  };

  const activate = async (row) => {
    try {
      await axios.post(`${API}/icra-permits/${row.id}/activate`, {}, { headers });
      toast.success(`${row.number} is active — print the door sign`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    }
  };

  const close = async (row) => {
    if (!window.confirm(`Close ${row.number}? Barriers must be inspected and the area terminally cleaned first.`)) return;
    try {
      await axios.post(`${API}/icra-permits/${row.id}/close`, {}, { headers });
      toast.success(`${row.number} closed`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not close");
    }
  };

  const logCheck = async () => {
    try {
      await axios.post(`${API}/icra-permits/${checking.id}/ilsm-check`,
        { items: checkItems, notes: checkNotes }, { headers });
      toast.success(`ILSM check logged for ${checking.number}`);
      setChecking(null); setCheckItems({}); setCheckNotes(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log the check");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.number}?`)) return;
    try {
      await axios.delete(`${API}/icra-permits/${row.id}`, { headers });
      toast.success("Permit deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const printSign = async (row) => {
    try {
      const base = encodeURIComponent(window.location.origin);
      const r = await axios.get(`${API}/icra-permits/${row.id}/sign-pdf?portal_base=${base}`,
        { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${row.number}-door-sign.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the door sign");
    }
  };

  if (loading) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-8">
      <Loader2 className="w-4 h-4 animate-spin" />Loading ICRA permits…</p>;
  }

  return (
    <div className="space-y-5" data-testid="icra-permits-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">ICRA / ILSM Permits</h2>
          <p className="text-sm text-slate-500">
            ASHE matrix: Activity Type A–D × Patient Risk Group 1–4 → Class of Precautions I–IV.
            Class III and IV need Infection Prevention sign-off before anyone breaks the ceiling.
          </p>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => {
            setEditingId(null);
            setForm({
              ...blank(),
              department: project?.facility || "",
              infection_prevention_contact: "",
              safety_contact: "",
            });
          }}
          data-testid="new-icra-permit-btn">
          <Plus className="w-4 h-4 mr-2" />New Permit
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="icra-summary">
          <Stat label="Permits" value={summary.total} sub={`${summary.closed} closed`} />
          <Stat label="Active" value={summary.active} accent="text-emerald-600" />
          <Stat label="Awaiting signature" value={summary.pending_signatures}
            accent={summary.pending_signatures ? "text-amber-600" : "text-slate-900"} />
          <Stat label="Class III / IV" value={summary.class_iii_iv} accent="text-red-600"
            sub="Infection Prevention required" />
          <Stat label="ILSM checks due" value={summary.ilsm_checks_due}
            accent={summary.ilsm_checks_due ? "text-red-600" : "text-slate-900"} sub="Today" />
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 bg-white"
          data-testid="icra-empty">
          <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">No ICRA permits issued yet</p>
          <p className="text-sm text-slate-400 mt-1">
            Issue one per work area. The permit prints as a door sign with a QR code to the live record.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
          <table className="w-full min-w-[1120px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["No.", "Work / location", "Type × Risk", "Class", "Dates", "Signatures", "ILSM", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/70" data-testid={`icra-row-${row.number}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-bold text-blue-700">{row.number}</span>
                  </td>
                  <td className="px-4 py-3 max-w-[290px]">
                    <p className="text-sm font-semibold text-slate-900 truncate">{row.work_description || "—"}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {[row.building, row.floor && `Fl ${row.floor}`, row.department, row.room_numbers]
                        .filter(Boolean).join(" · ") || "—"}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    <span className="font-bold">{row.activity_type}</span> × {row.patient_risk_group}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-black ring-1 ${CLASS_STYLE[row.precaution_class] || CLASS_STYLE.II}`}>
                      Class {row.precaution_class}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {row.start_date || "—"} → {row.end_date || "—"}
                    {row.is_expired ? (
                      <span className="block text-[10px] font-bold text-red-600">EXPIRED</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {row.fully_signed ? (
                      <span className="text-emerald-700 font-bold inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />Complete
                      </span>
                    ) : (
                      <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" />{row.missing_signatures.length} missing
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {row.ilsm_required ? (
                      <>
                        {row.ilsm_check_count} check(s)
                        {row.ilsm_check_due ? (
                          <span className="block text-[10px] font-bold text-red-600">DUE TODAY</span>
                        ) : null}
                      </>
                    ) : <span className="text-slate-400">N/A</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_STYLE[row.status]}`}>
                      {(row.status || "").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {!["closed", "void"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-slate-700 hover:bg-slate-100"
                          onClick={() => {
                            setSigning(row);
                            setSign({ role: row.missing_signatures[0] || "infection_prevention", name: "", title: "" });
                          }}
                          data-testid={`icra-sign-${row.number}`}>
                          <PenLine className="w-3.5 h-3.5 mr-1" />Sign
                        </Button>
                      )}
                      {row.status !== "active" && !["closed", "void"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => activate(row)} data-testid={`icra-activate-${row.number}`}>
                          Activate
                        </Button>
                      )}
                      {row.status === "active" && row.ilsm_required && (
                        <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50"
                          onClick={() => { setChecking(row); setCheckItems({}); setCheckNotes(""); }}
                          data-testid={`icra-ilsm-${row.number}`}>
                          <ClipboardCheck className="w-3.5 h-3.5 mr-1" />ILSM
                        </Button>
                      )}
                      {row.status === "active" && (
                        <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                          onClick={() => close(row)} data-testid={`icra-close-${row.number}`}>
                          <Lock className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                        onClick={() => printSign(row)} title="Print door sign"
                        data-testid={`icra-sign-pdf-${row.number}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                        onClick={() => setPreview(row)} title="Precautions and QR link"
                        data-testid={`icra-preview-${row.number}`}>
                        <QrCode className="w-3.5 h-3.5" />
                      </Button>
                      {!["active", "closed"].includes(row.status) && (
                        <>
                          <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                            onClick={() => { setEditingId(row.id); setForm({ ...blank(), ...row }); }}>Edit</Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                            onClick={() => remove(row)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Permit form */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit ICRA permit" : "Issue an ICRA permit"}</DialogTitle>
            <DialogDescription>
              Pick the activity type and the patient risk group of the surrounding area — the class of
              precautions is set by the ASHE matrix, not by choice.
            </DialogDescription>
          </DialogHeader>
          {form && meta && (
            <div className="space-y-4">
              <div>
                <Label>Work being permitted</Label>
                <Textarea rows={2} value={form.work_description}
                  placeholder="Demolish ceiling grid in rooms 2-114 to 2-118 and reroute med gas above corridor 2A"
                  onChange={(e) => setForm({ ...form, work_description: e.target.value })}
                  data-testid="icra-work-input" />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Construction activity type</Label>
                  <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                    <SelectTrigger data-testid="icra-activity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(meta.activity_types || []).map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    {(meta.activity_types || []).find((t) => t.value === form.activity_type)?.description}
                  </p>
                </div>
                <div>
                  <Label>Patient risk group of the adjacent area</Label>
                  <Select value={String(form.patient_risk_group)}
                    onValueChange={(v) => setForm({ ...form, patient_risk_group: v })}>
                    <SelectTrigger data-testid="icra-risk-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(meta.risk_groups || []).map((g) => (
                        <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    {(meta.risk_groups || []).find((g) => g.value === String(form.patient_risk_group))?.areas}
                  </p>
                </div>
              </div>

              {liveClass && (
                <div className={`rounded-lg ring-1 p-3 ${CLASS_STYLE[liveClass] || CLASS_STYLE.II}`}
                  data-testid="icra-live-class">
                  <p className="text-xs font-black uppercase tracking-wider">
                    Class {liveClass} precautions apply
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {(liveClass === "III/IV" ? ["III", "IV"] : [liveClass]).flatMap((k) =>
                      (meta.class_precautions?.[k]?.during || [])).slice(0, 4).map((line, i) => (
                        <li key={i} className="text-[11px] leading-snug">• {line}</li>
                      ))}
                  </ul>
                  {["III", "IV", "III/IV"].includes(liveClass) && (
                    <p className="text-[11px] font-bold mt-1.5">
                      Infection Prevention signature required before this permit can go active.
                    </p>
                  )}
                </div>
              )}

              <div className="grid sm:grid-cols-4 gap-3">
                <div><Label>Building</Label>
                  <Input value={form.building} onChange={(e) => setForm({ ...form, building: e.target.value })} /></div>
                <div><Label>Floor</Label>
                  <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} /></div>
                <div><Label>Department</Label>
                  <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                <div><Label>Room numbers</Label>
                  <Input value={form.room_numbers} onChange={(e) => setForm({ ...form, room_numbers: e.target.value })}
                    data-testid="icra-rooms-input" /></div>
              </div>

              <div className="grid sm:grid-cols-4 gap-3">
                <div><Label>Adjacent areas</Label>
                  <Input value={form.adjacent_areas} placeholder="NICU above, corridor 2A"
                    onChange={(e) => setForm({ ...form, adjacent_areas: e.target.value })} /></div>
                <div><Label>Start date</Label>
                  <Input type="date" value={form.start_date || ""}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    data-testid="icra-start-input" /></div>
                <div><Label>End date</Label>
                  <Input type="date" value={form.end_date || ""}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                <div><Label>Work hours</Label>
                  <Input value={form.work_hours} placeholder="7pm – 5am"
                    onChange={(e) => setForm({ ...form, work_hours: e.target.value })} /></div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Contractor</Label>
                  <Input value={form.responsible_company}
                    onChange={(e) => setForm({ ...form, responsible_company: e.target.value })} /></div>
                <div><Label>Foreman</Label>
                  <Input value={form.contractor_foreman}
                    onChange={(e) => setForm({ ...form, contractor_foreman: e.target.value })} /></div>
                <div><Label>Foreman phone</Label>
                  <Input value={form.foreman_phone} placeholder="281-456-3458"
                    onChange={(e) => setForm({ ...form, foreman_phone: e.target.value })} /></div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Infection Prevention contact</Label>
                  <Input value={form.infection_prevention_contact}
                    onChange={(e) => setForm({ ...form, infection_prevention_contact: e.target.value })} /></div>
                <div><Label>Safety / Facilities contact</Label>
                  <Input value={form.safety_contact}
                    onChange={(e) => setForm({ ...form, safety_contact: e.target.value })} /></div>
              </div>

              <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Containment &amp; routes</p>
                <div className="grid sm:grid-cols-4 gap-3">
                  <div><Label>Barrier type</Label>
                    <Input value={form.barrier_type} placeholder="Hard barrier, sealed"
                      onChange={(e) => setForm({ ...form, barrier_type: e.target.value })} /></div>
                  <div><Label>Negative air units</Label>
                    <Input type="number" value={form.negative_air_units}
                      onChange={(e) => setForm({ ...form, negative_air_units: e.target.value })} /></div>
                  <div><Label>Debris route</Label>
                    <Input value={form.debris_route} placeholder="Service elevator 3, after 8pm"
                      onChange={(e) => setForm({ ...form, debris_route: e.target.value })} /></div>
                  <div><Label>Worker traffic route</Label>
                    <Input value={form.traffic_route} placeholder="Loading dock → stair B"
                      onChange={(e) => setForm({ ...form, traffic_route: e.target.value })} /></div>
                </div>
                <div className="flex flex-wrap gap-4">
                  {[
                    ["hepa_filtration", "HEPA filtration in place"],
                    ["ilsm_required", "ILSM measures required (daily check)"],
                    ["fire_alarm_impairment", "Fire alarm impairment"],
                    ["water_shutdown", "Water shutdown planned"],
                  ].map(([key, label]) => (
                    <label key={key} className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={!!form[key]}
                        onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                        data-testid={`icra-${key}-checkbox`} />
                      {label}
                    </label>
                  ))}
                </div>
                <div>
                  <Label>Additional requirements printed on the sign</Label>
                  <Textarea rows={2} value={form.additional_requirements}
                    placeholder="N95 required inside the barrier. No cart traffic through the nursery corridor."
                    onChange={(e) => setForm({ ...form, additional_requirements: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}
              data-testid="icra-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save permit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature */}
      <Dialog open={!!signing} onOpenChange={(o) => !o && setSigning(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign {signing?.number}</DialogTitle>
            <DialogDescription>
              Class {signing?.precaution_class} precautions.
              {signing?.missing_signatures?.length
                ? ` Still missing: ${signing.missing_signatures.map((r) => r.replace(/_/g, " ")).join(", ")}.`
                : " All required signatures are in."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Signing as</Label>
              <Select value={sign.role} onValueChange={(v) => setSign({ ...sign, role: v })}>
                <SelectTrigger data-testid="icra-sign-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.signature_roles || []).map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={sign.name} onChange={(e) => setSign({ ...sign, name: e.target.value })}
                data-testid="icra-sign-name-input" />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={sign.title} placeholder="Infection Preventionist, RN"
                onChange={(e) => setSign({ ...sign, title: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigning(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={doSign}
              data-testid="icra-sign-submit-btn">Record signature</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ILSM daily check */}
      <Dialog open={!!checking} onOpenChange={(o) => !o && setChecking(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>ILSM daily check — {checking?.number}</DialogTitle>
            <DialogDescription>
              Interim Life Safety Measures verification. One check per day; unchecked items are recorded as deficiencies.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(meta?.ilsm_items || []).map((item) => (
              <label key={item.key} className="flex items-start gap-2 text-sm text-slate-700 py-1">
                <input type="checkbox" className="mt-1" checked={!!checkItems[item.key]}
                  onChange={(e) => setCheckItems({ ...checkItems, [item.key]: e.target.checked })}
                  data-testid={`ilsm-${item.key}`} />
                <span>{item.label}</span>
              </label>
            ))}
            <div className="pt-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={checkNotes} placeholder="Barrier intact, dust mat replaced."
                onChange={(e) => setCheckNotes(e.target.value)} data-testid="ilsm-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChecking(null)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={logCheck}
              data-testid="ilsm-submit-btn">Log today's check</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Precautions + QR link */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{preview?.number} · Class {preview?.precaution_class}</DialogTitle>
            <DialogDescription>{preview?.work_description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Required during construction</p>
              <ul className="space-y-1">
                {(preview?.precautions?.during || []).map((l, i) => (
                  <li key={i} className="text-slate-700 text-[13px] leading-snug">• {l}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Required upon completion</p>
              <ul className="space-y-1">
                {(preview?.precautions?.after || []).map((l, i) => (
                  <li key={i} className="text-slate-700 text-[13px] leading-snug">• {l}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Live permit link (QR target)</p>
              <a className="text-blue-700 break-all text-[13px] hover:underline"
                href={`/permit/${preview?.public_token}`} target="_blank" rel="noreferrer"
                data-testid="icra-public-link">
                {window.location.origin}/permit/{preview?.public_token}
              </a>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => printSign(preview)}>
              <Download className="w-4 h-4 mr-2" />Print door sign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
